import { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Sparkles, MapPin, Calendar, Users, ArrowRight, Bot, User as UserIcon, Loader2, LogIn, ChevronDown, Eye, EyeOff, Mail, Lock, UserPlus } from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import { useLocation } from '@context/LocationContext';
import { Link, useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { getCookie, setCookie } from '@utils/cookies';
import { formatCountNoun } from '@utils/helpers';
import api from '@utils/api';
import { trackLogin } from '@utils/analytics';
import { signupEntryPath } from '@utils/authEntryPaths';
import toast from 'react-hot-toast';

const SHERI_TOAST_KEY = 'pf_sheri_toast_shown';

const SUGGESTIONS = [
  { text: 'Find games nearby', icon: MapPin },
  { text: 'Create a match tonight', icon: Calendar },
  { text: "Who's looking for partners?", icon: Users },
  { text: 'What vibes are happening?', icon: Sparkles },
];

const GUEST_SUGGESTIONS = [
  { text: 'What is Yaarvi?', icon: Sparkles },
  { text: 'What vibes are nearby?', icon: MapPin },
  { text: 'How do I create a match?', icon: Calendar },
  { text: 'How does it work?', icon: Users },
];

const ActivityCard = ({ activity, onNavigate }) => (
  <button
    onClick={() => onNavigate(`/activity/${activity._id}`)}
    className="w-full text-left flex items-center gap-3 p-3 rounded-xl bg-white/60 dark:bg-surface-800/60 border border-surface-200/50 dark:border-surface-700/50 hover:bg-white dark:hover:bg-surface-800 transition-all group"
  >
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-violet-600 text-lg shadow-sm">
      {activity.emoji || '🎯'}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-bold text-surface-900 dark:text-white truncate">{activity.title}</p>
      <p className="text-[10px] text-surface-500 mt-0.5">
        {activity.spotsLeft > 0 ? `${formatCountNoun(activity.spotsLeft, 'spot')} left` : 'Full'} · {activity.address || 'Nearby'}
      </p>
    </div>
    <ArrowRight size={14} className="text-surface-400 group-hover:text-brand-500 transition-colors shrink-0" />
  </button>
);

const ChatMessage = ({ msg, onNavigate }) => {
  const isUser = msg.role === 'user';

  return (
    <div className={`flex gap-2.5 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
        isUser
          ? 'bg-brand-500 text-white'
          : 'bg-gradient-to-br from-violet-500 to-brand-500 text-white'
      }`}>
        {isUser ? <UserIcon size={13} /> : <Bot size={13} />}
      </div>
      <div className={`max-w-[80%] space-y-2 ${isUser ? 'items-end' : ''}`}>
        <div className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
          isUser
            ? 'bg-brand-500 text-white rounded-br-md'
            : 'bg-white dark:bg-surface-800 text-surface-700 dark:text-surface-300 border border-surface-200/60 dark:border-surface-700/60 rounded-bl-md shadow-sm'
        }`}>
          <p className="whitespace-pre-wrap">{msg.content}</p>
        </div>

        {msg.activities?.length > 0 && (
          <div className="space-y-1.5">
            {msg.activities.map((a) => (
              <ActivityCard key={a._id} activity={a} onNavigate={onNavigate} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const TypingIndicator = () => (
  <div className="flex gap-2.5">
    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-brand-500 text-white">
      <Bot size={13} />
    </div>
    <div className="bg-white dark:bg-surface-800 border border-surface-200/60 dark:border-surface-700/60 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
      <div className="flex gap-1.5">
        <span className="h-2 w-2 rounded-full bg-surface-400 animate-bounce [animation-delay:0ms]" />
        <span className="h-2 w-2 rounded-full bg-surface-400 animate-bounce [animation-delay:150ms]" />
        <span className="h-2 w-2 rounded-full bg-surface-400 animate-bounce [animation-delay:300ms]" />
      </div>
    </div>
  </div>
);

const InlinLoginForm = ({ onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await login(email.trim(), password);
      trackLogin('email');
      toast.success('Signed in! You can now create & join vibes.');
      onSuccess?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Check your credentials.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="my-3 rounded-xl border border-surface-200/60 bg-white p-4 dark:border-surface-700/60 dark:bg-surface-800 space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <LogIn size={14} className="text-brand-500" />
        <p className="text-xs font-bold text-surface-700 dark:text-surface-300">Sign in to continue</p>
      </div>

      {error && (
        <p className="text-[11px] text-red-500 bg-red-50 dark:bg-red-500/10 rounded-lg px-3 py-1.5">{error}</p>
      )}

      <div className="relative">
        <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          autoComplete="email"
          className="w-full rounded-lg border border-surface-200/60 bg-surface-50 pl-9 pr-3 py-2 text-xs text-surface-900 placeholder-surface-400 outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 dark:bg-surface-900 dark:border-surface-700/60 dark:text-white"
        />
      </div>

      <div className="relative">
        <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
        <input
          type={showPw ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          autoComplete="current-password"
          className="w-full rounded-lg border border-surface-200/60 bg-surface-50 pl-9 pr-9 py-2 text-xs text-surface-900 placeholder-surface-400 outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 dark:bg-surface-900 dark:border-surface-700/60 dark:text-white"
        />
        <button
          type="button"
          onClick={() => setShowPw(!showPw)}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600"
        >
          {showPw ? <EyeOff size={13} /> : <Eye size={13} />}
        </button>
      </div>

      <button
        type="submit"
        disabled={submitting || !email.trim() || !password.trim()}
        className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-brand-500 px-3 py-2 text-xs font-bold text-white shadow-sm hover:bg-brand-600 disabled:opacity-50 transition-colors"
      >
        {submitting ? <Loader2 size={13} className="animate-spin" /> : <LogIn size={13} />}
        {submitting ? 'Signing in...' : 'Sign in'}
      </button>

      <p className="text-[10px] text-center text-surface-500">
        Don't have an account?{' '}
        <Link to={signupEntryPath()} className="text-brand-500 font-medium hover:underline">
          Sign up
        </Link>
      </p>
    </form>
  );
};

const AIChatAssistant = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const { user } = useAuth();
  const { location: userLoc, locationName: userLocationName } = useLocation();
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const path = routerLocation.pathname;
  /** Compact icon-only FAB (activity detail, create, edit, inbox) — full “Ask Sheri” pill elsewhere. */
  const isCompactSheriFab =
    /^\/activity\/[^/]+$/.test(path) ||
    path === '/create' ||
    /^\/activity\/[^/]+\/edit$/.test(path) ||
    path.startsWith('/messages');

  // Close chat on route change
  useEffect(() => {
    setOpen(false);
  }, [routerLocation.pathname]);

  useEffect(() => {
    if (getCookie(SHERI_TOAST_KEY)) return;
    const timer = setTimeout(() => {
      toast(
        'Make your life easier — talk to Sheri to find or schedule a vibe! ✨',
        { duration: 5000, icon: '💬', style: { fontSize: '13px', maxWidth: '360px' } }
      );
      setCookie(SHERI_TOAST_KEY, '1', 30);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, scrollToBottom]);

  const handleNavigate = useCallback((path) => {
    setOpen(false);
    navigate(path);
  }, [navigate]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || loading) return;

    const userMsg = { role: 'user', content: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const conversationHistory = messages.slice(-10).map(({ role, content, activities }) => {
        const entry = { role, content };
        if (activities?.length) entry.activities = activities;
        return entry;
      });

      const { data } = await api.post('/ai-chat', {
        message: trimmed,
        location: {
          latitude: userLoc?.lat || 0,
          longitude: userLoc?.lng || 0,
          locationName: userLocationName || '',
        },
        conversationHistory,
      });

      const activities =
        data.toolResults?.flatMap((r) => {
          const res = r.result;
          if (!res?.type) return [];
          if (res.type === 'intents' && Array.isArray(res.openActivitiesData)) {
            return res.openActivitiesData;
          }
          if (res.type === 'activities' || res.type === 'activity_detail' || res.type === 'activity_created') {
            if (!res.data) return [];
            return Array.isArray(res.data) ? res.data : [res.data];
          }
          return [];
        }) || [];

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.message,
          activities,
        },
      ]);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: 'You need to sign in to use this feature. Please log in or create an account to continue.', needsLogin: true },
        ]);
      } else {
        const errorMsg = err.response?.data?.message || 'Something went wrong. Please try again.';
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: errorMsg },
        ]);
      }
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, userLoc, userLocationName]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestions = user ? SUGGESTIONS : GUEST_SUGGESTIONS;
  const greeting = user
    ? `Hey ${user.name?.split(' ')[0]}! I'm Sheri 👋`
    : "Hey! I'm Sheri 👋";
  const subtitle = user
    ? 'I can help you find games, create matches, and more.'
    : 'I can help you discover vibes nearby. Sign in to create or join matches!';

  if (['/welcome', '/login', '/register'].includes(routerLocation.pathname)) {
    return null;
  }

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-[1100] bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* FAB — hidden when chat is open */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-4 lg:bottom-6 lg:right-6 z-[998] group"
          aria-label="Talk to Sheri"
        >
          {isCompactSheriFab ? (
            <div className="relative flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-violet-600 shadow-md shadow-brand-500/20 ring-1 ring-white/15 hover:shadow-lg hover:shadow-brand-500/30 hover:scale-105 active:scale-95 transition-all">
              <MessageCircle size={18} className="text-white" strokeWidth={2} />
              <span className="pointer-events-none absolute -right-px -top-px flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full border border-white bg-emerald-500 dark:border-surface-900" />
              </span>
            </div>
          ) : (
            <div className="relative flex items-center gap-1.5 rounded-full bg-gradient-to-r from-brand-500 to-violet-600 py-1.5 pl-2 pr-2.5 shadow-md shadow-brand-500/20 ring-1 ring-white/10 hover:shadow-lg hover:shadow-brand-500/25 hover:scale-[1.02] active:scale-[0.98] transition-all">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/20">
                <MessageCircle size={13} className="text-white" strokeWidth={2} />
              </div>
              <span className="whitespace-nowrap pr-0.5 text-[11px] font-semibold tracking-tight text-white">
                Ask Sheri
              </span>
              <span className="pointer-events-none absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full border border-white bg-green-500 dark:border-surface-900" />
              </span>
            </div>
          )}
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed z-[1101] inset-x-0 bottom-0 lg:inset-auto lg:bottom-6 lg:right-6 lg:w-[380px]">
          <div className="flex flex-col h-[70vh] max-h-[600px] lg:h-[480px] lg:max-h-[calc(100vh-100px)] rounded-t-2xl lg:rounded-2xl border border-surface-200/60 bg-surface-50 shadow-2xl dark:border-surface-700/60 dark:bg-surface-900">

            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-brand-500 to-violet-600 shrink-0 rounded-t-2xl lg:rounded-t-2xl">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 backdrop-blur text-base">
                🤖
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold text-white">Sheri</h3>
                <p className="text-[10px] text-white/70">Your Yaarvi assistant</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white/80 hover:bg-white/25 hover:text-white transition-colors"
                aria-label="Close chat"
              >
                <ChevronDown size={18} />
              </button>
            </div>

            {/* Messages area — this is the only scrollable section */}
            <div
              ref={scrollRef}
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-3 space-y-4 scrollbar-hide"
            >
              {/* Inline login for guests */}
              {!user && messages.length === 0 && (
                <InlinLoginForm onSuccess={() => {
                  setMessages((prev) => [
                    ...prev,
                    { role: 'assistant', content: "You're all set! Now you can create matches, join vibes, and more. What would you like to do?" },
                  ]);
                }} />
              )}

              {messages.length === 0 && !loading && (
                <div className="text-center py-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-brand-500/10 to-violet-500/10 mx-auto mb-3 text-xl">
                    🤖
                  </div>
                  <p className="text-sm font-bold text-surface-700 dark:text-surface-300 mb-1">
                    {greeting}
                  </p>
                  <p className="text-xs text-surface-500 mb-4">
                    {subtitle}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {suggestions.map(({ text, icon: Icon }) => (
                      <button
                        key={text}
                        onClick={() => sendMessage(text)}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white dark:bg-surface-800 border border-surface-200/60 dark:border-surface-700/60 text-left text-[11px] font-medium text-surface-600 dark:text-surface-400 hover:bg-brand-50 hover:text-brand-600 dark:hover:bg-brand-500/10 dark:hover:text-brand-400 hover:border-brand-200 dark:hover:border-brand-500/20 transition-all"
                      >
                        <Icon size={12} className="text-brand-500 shrink-0" />
                        <span className="line-clamp-2">{text}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <div key={i}>
                  <ChatMessage msg={msg} onNavigate={handleNavigate} />
                  {msg.needsLogin && (
                    <InlinLoginForm onSuccess={() => {
                      setMessages((prev) => [
                        ...prev,
                        { role: 'assistant', content: "You're signed in now! Let me try that again for you." },
                      ]);
                    }} />
                  )}
                </div>
              ))}

              {loading && <TypingIndicator />}
            </div>

            {/* Input bar — fixed at bottom of panel */}
            <div className="border-t border-surface-200/60 dark:border-surface-700/60 px-3 py-2.5 bg-white dark:bg-surface-900 shrink-0 rounded-b-none lg:rounded-b-2xl">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask Sheri anything..."
                  className="flex-1 rounded-xl border border-surface-200/60 bg-surface-50 px-3.5 py-2.5 text-sm text-surface-900 placeholder-surface-400 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 dark:bg-surface-800 dark:border-surface-700/60 dark:text-white dark:placeholder-surface-500 transition-all"
                  disabled={loading}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || loading}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-violet-600 text-white shadow-sm disabled:opacity-40 disabled:shadow-none transition-all hover:shadow-md active:scale-95"
                >
                  {loading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Send size={16} />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatAssistant;
